/**
 * Chat API Client — mobile app API functions for the chat assistant.
 */

import { getApiBase } from "@/lib/api-env";

async function request<T>(path: string, options?: RequestInit): Promise<T> {
    const res = await fetch(`${getApiBase()}${path}`, {
        ...options,
        headers: {
            "Content-Type": "application/json",
            ...options?.headers
        }
    });

    if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error?.message ?? body.error ?? `Request failed: ${res.status}`);
    }

    return res.json();
}

// ─── Types ───────────────────────────────────────────

export type ChatResponseBlock =
    | { type: "text"; text: string }
    | { type: "quick_replies"; options: QuickReplyOption[] }
    | { type: "visit_cards"; visits: VisitCardData[] }
    | { type: "action_confirm"; action: { label: string; description: string } };

export interface QuickReplyOption {
    label: string;
    value: string;
    icon?: string;
}

export interface VisitCardData {
    id: string;
    patientName: string;
    time: string;
    status: string;
    address?: string;
}

export interface Conversation {
    id: string;
    rnId: string;
    agencyId: string;
    status: "active" | "escalated" | "resolved";
    initiatedBy: string;
    botState: Record<string, unknown> | null;
    visitId: string | null;
    escalatedReason: string | null;
    aiSummary: string | null;
    createdAt: string;
    updatedAt: string;
    lastMessage?: ChatMessage | null;
    unreadCount?: number;
}

export interface ChatMessage {
    id: string;
    conversationId: string;
    role: "rn" | "bot" | "ai" | "human_agent" | "system";
    senderId: string | null;
    senderName: string | null;
    senderType: string | null;
    content: string | null;
    responseBlocks: ChatResponseBlock[] | null;
    llmModel: string | null;
    llmLatencyMs: number | null;
    createdAt: string;
    deliveredAt: string | null;
    readAt: string | null;
}

export type DeliveryStatus = "sending" | "sent" | "delivered" | "failed";

export interface LocalMessage extends ChatMessage {
    deliveryStatus: DeliveryStatus;
    localId: string; // client-side ID for optimistic UI
}

// ─── API Functions ───────────────────────────────────

export function createConversation(
    rnId: string,
    agencyId: string
): Promise<{ conversation: Conversation; messages: ChatMessage[] }> {
    return request("/chat/conversations", {
        method: "POST",
        body: JSON.stringify({ rnId, agencyId })
    });
}

export function fetchConversations(rnId: string, status?: string): Promise<Conversation[]> {
    const qs = status ? `?rnId=${rnId}&status=${status}` : `?rnId=${rnId}`;
    return request(`/chat/conversations${qs}`);
}

export function fetchMessages(
    conversationId: string
): Promise<{ conversation: Conversation; messages: ChatMessage[] }> {
    return request(`/chat/conversations/${conversationId}`);
}

export function sendMessage(
    conversationId: string,
    content: string,
    rnId: string
): Promise<{
    userMessage: ChatMessage;
    response: ChatMessage | null;
    handledBy: "bot" | "ai" | "queued";
}> {
    return request(`/chat/conversations/${conversationId}/messages`, {
        method: "POST",
        body: JSON.stringify({ content, rnId })
    });
}

export function markAsRead(conversationId: string): Promise<{ read: boolean }> {
    return request(`/chat/conversations/${conversationId}/read`, {
        method: "POST",
        body: "{}"
    });
}

export function resolveConversation(conversationId: string): Promise<{ resolved: boolean }> {
    return request(`/chat/conversations/${conversationId}/resolve`, {
        method: "POST",
        body: "{}"
    });
}
